import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import SidebarSubmenu from './SidebarSubmenu';

const SidebarItem = ({ 
  item, 
  isActive, 
  isExpanded, 
  collapsed, 
  activeSubTab, 
  currentAction,
  onItemClick, 
  onSubItemClick 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const itemRef = useRef(null);
  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;

  // Scroll active item smoothly into view within navigation container
  useEffect(() => {
    if (isActive && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isActive]);

  return (
    <div style={{ position: 'relative', margin: '1px 0' }}>
      <button
        ref={itemRef}
        type="button"
        className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
        onClick={() => onItemClick(item)}
        aria-expanded={hasChildren && !collapsed ? isExpanded : undefined}
        aria-current={isActive && !hasChildren ? 'page' : undefined}
        aria-label={item.label}
        onMouseEnter={() => collapsed && setShowTooltip(true)}
        onMouseLeave={() => collapsed && setShowTooltip(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: collapsed ? '100%' : 'calc(100% - 16px)',
          height: '40px',
          margin: collapsed ? '1px 0' : '1px 8px',
          padding: collapsed ? '0' : '0 10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: isActive ? '#eff6ff' : 'transparent',
          color: isActive ? '#2563eb' : '#334155',
          fontWeight: isActive ? '600' : '500',
          fontSize: '13.5px',
          border: 'none',
          borderRadius: '7px',
          borderLeft: isActive ? '3px solid #2563eb' : '3px solid transparent',
          cursor: 'pointer',
          transition: 'all 0.12s ease',
          outline: 'none',
          userSelect: 'none',
          boxSizing: 'border-box'
        }}
        onMouseOver={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.color = '#0f172a';
          }
        }}
        onMouseOut={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#334155';
          }
        }}
      >
        {/* Icon */}
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          color: isActive ? '#2563eb' : '#64748b',
          flexShrink: 0,
          transition: 'color 0.12s ease'
        }}>
          {Icon && <Icon size={18} strokeWidth={2} />}
        </span>

        {/* Label (Hidden in Collapsed Mode) */}
        {!collapsed && (
          <span style={{ marginLeft: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: 'left' }}>
            {item.label}
          </span>
        )}

        {/* Chevron Icon for Expandable Parent Items */}
        {hasChildren && !collapsed && (
          <span style={{ 
            marginLeft: 'auto', 
            color: (isExpanded || isActive) ? '#2563eb' : '#94a3b8', 
            display: 'flex', 
            alignItems: 'center', 
            transition: 'transform 150ms ease, color 150ms ease' 
          }}>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        )}
      </button>

      {/* Tooltip for Collapsed Mode */}
      {collapsed && showTooltip && (
        <div style={{
          position: 'absolute',
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginLeft: '10px',
          background: '#0f172a',
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: '500',
          padding: '5px 10px',
          borderRadius: '6px',
          whiteSpace: 'nowrap',
          zIndex: 9999,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none'
        }}>
          {item.label}
        </div>
      )}

      {/* Submenu Item */}
      {hasChildren && (
        <SidebarSubmenu
          children={item.children}
          expanded={isExpanded}
          collapsed={collapsed}
          activeSubTab={activeSubTab}
          currentAction={currentAction}
          onSubItemClick={onSubItemClick}
        />
      )}
    </div>
  );
};

export default SidebarItem;
